import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import patientBlisterMedicationsService, {
  BlisterMedicationsResponse,
  ManualBillingPin,
  UpdateBlisterMedicationPayload,
} from '../services/patientBlisterMedicationsService';
import { patientBlisterQueryKey, usePatientBlister } from './usePatientBlister';

/**
 * PRX-200 (review M4): only the GET reads Fillware for strength, directions and
 * the latest fill. A write or seed answers the grid without them, so when the
 * cache holds a GET's Fillware details they are carried onto the answer by Rx
 * number. A row the GET never saw is marked as from the order, never left
 * looking like Fillware's.
 */
export function carryFillwareDetails(
  previous: BlisterMedicationsResponse | undefined,
  next: BlisterMedicationsResponse,
): BlisterMedicationsResponse {
  if (next.fillwareRx !== undefined || previous?.fillwareRx === undefined) return next;
  const byRx = new Map(previous.rows.map((row) => [row.rxNumber, row]));
  return {
    ...next,
    fillwareRx: previous.fillwareRx,
    rows: next.rows.map((row) => {
      const seen = byRx.get(row.rxNumber);
      if (!seen) return { ...row, detailsSource: 'order', lastFillDate: null, fillwareDispensable: null };
      return {
        ...row,
        strength: seen.strength,
        directions: seen.directions,
        lastFillDate: seen.lastFillDate,
        detailsSource: seen.detailsSource,
        fillwareDispensable: seen.fillwareDispensable,
      };
    }),
  };
}

/** One key for the grid, so a save and a seed repaint the same rows. */
export const patientBlisterMedicationsQueryKey = (pharmacyId?: string, patientId?: string) =>
  ['patient-blister-medications', pharmacyId, patientId] as const;

/**
 * The one definition of the per-drug basket read: key, fetch and retry rule.
 * Two observers share it (PRX-137 review, M2):
 * - the Blister pack grid (`usePatientBlisterMedications` below), which asks
 *   only for a patient on packs;
 * - the Prescriptions tab (`tabs/prescriptions/useRxBasketLines`), which asks
 *   for any patient to learn which Rx sit on a subscription basket.
 *
 * The server read serves both: it reports `blisterPack` and returns every live
 * basket line, without filtering on the flag. Each caller adds only its own
 * `enabled`. Because the key is shared, a write from either place repaints both.
 */
export function patientBlisterMedicationsQueryOptions(pharmacyId?: string, patientId?: string) {
  return {
    queryKey: patientBlisterMedicationsQueryKey(pharmacyId, patientId),
    queryFn: (): Promise<BlisterMedicationsResponse> =>
      patientBlisterMedicationsService.list(pharmacyId as string, patientId as string),
    // A 4xx here is a settled answer (no PHOX row, or no permission), not a
    // blip: retrying doubles the request and buys nothing.
    retry: (failureCount: number, error: unknown) => {
      const status = (error as any)?.response?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  };
}

/**
 * PRX-47: the Blister pack grid's rows.
 *
 * The grid asks only for a patient on blister packs. That rule is enforced
 * here, through `enabled`, rather than depending on where the grid is mounted:
 * no flag means no request, and while the flag is in flight the hook waits
 * rather than guessing. Reading `usePatientBlister` is a cache hit, because the
 * Patient 360 page already read it on mount.
 *
 * The Prescriptions tab reads the same rows for any patient, through
 * `patientBlisterMedicationsQueryOptions` (above), with its own `enabled`.
 */
export function usePatientBlisterMedications(pharmacyId?: string, patientId?: string) {
  const { data: blister } = usePatientBlister(pharmacyId, patientId);
  const onBlisterPacks = blister?.blisterPack === true;

  return useQuery({
    ...patientBlisterMedicationsQueryOptions(pharmacyId, patientId),
    enabled: !!pharmacyId && !!patientId && onBlisterPacks,
  });
}

/**
 * One drug's settings.
 *
 * The server's answer replaces the cache, so a row always shows what was
 * actually stored — never what the client hoped it wrote. On failure nothing is
 * written to the cache and the caller keeps the user's selection on screen with
 * the server's sentence beside it, rather than silently snapping the control
 * back.
 */
export function useUpdateBlisterMedication(pharmacyId?: string, patientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { rxNumber: string; payload: UpdateBlisterMedicationPayload }) =>
      patientBlisterMedicationsService.update(
        pharmacyId as string, patientId as string, variables.rxNumber, variables.payload,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(
        patientBlisterMedicationsQueryKey(pharmacyId, patientId),
        (previous: BlisterMedicationsResponse | undefined) => carryFillwareDetails(previous, data),
      );
      // PRX-127: a slot or placement change can move the derived pack size,
      // which the pack schedule card and the SYN dates read off the settings.
      queryClient.invalidateQueries({ queryKey: patientBlisterQueryKey(pharmacyId, patientId) });
    },
  });
}

/**
 * PRX-103 part 2 — add an Rx to the manual billing list, or remove it. The
 * server answers the whole grid, which replaces the cache; the pharmacy-wide
 * Blister pack list reads the same flag, so it is refetched too.
 */
export function useSetManualBilling(pharmacyId?: string, patientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { rxNumber: string; onList: boolean; pin: ManualBillingPin }) =>
      patientBlisterMedicationsService.setManualBilling(
        pharmacyId as string, patientId as string, variables.rxNumber, variables.onList, variables.pin,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(
        patientBlisterMedicationsQueryKey(pharmacyId, patientId),
        (previous: BlisterMedicationsResponse | undefined) => carryFillwareDetails(previous, data),
      );
      queryClient.invalidateQueries({ queryKey: ['blister-worklist'] });
    },
  });
}

/** The explicit "Seed from Fillware" action. */
export function useSeedBlisterMedications(pharmacyId?: string, patientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // A boolean is `force`, as before; `{ slotsOnly: true }` is "Fill slots
    // from Fillware" (WP-18), which never seeds a placement.
    mutationFn: (options?: boolean | { slotsOnly: true }) =>
      (typeof options === 'object'
        ? patientBlisterMedicationsService.seed(pharmacyId as string, patientId as string, false, true)
        : patientBlisterMedicationsService.seed(pharmacyId as string, patientId as string, options)),
    onSuccess: (data) => {
      queryClient.setQueryData(
        patientBlisterMedicationsQueryKey(pharmacyId, patientId),
        (previous: BlisterMedicationsResponse | undefined) => carryFillwareDetails(previous, data),
      );
    },
  });
}

/**
 * Throw away what the grid is showing and read it again.
 *
 * The 409 path: the server refused a write because the row moved under us (the
 * basket line the grid pinned is no longer that Rx, or the same Rx went live on
 * a second basket). Re-rendering the stale rows would invite the same doomed
 * save, so the pin is refreshed from the server before staff try again.
 */
export function useRefreshBlisterMedications(pharmacyId?: string, patientId?: string) {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: patientBlisterMedicationsQueryKey(pharmacyId, patientId),
    });
  };
}
